import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table } from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';
import { dayLabel } from '../utils/formatDate';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

// Chart.js draws to a <canvas>, which doesn't resolve CSS custom properties — literal values of the --mm- tokens.
// Entregas (our truck) in the brand cyan; retiros in the muted slate, so the two read as one palette.
const CYAN = '#12B8B2';
const SLATE = 'rgba(157,179,194,0.55)';
const MUTED = '#9DB3C2';
const LINE = 'rgba(234,242,245,0.10)';

const CHART_OPTIONS = {
  maintainAspectRatio: false,
  plugins: { legend: { labels: { color: MUTED, boxWidth: 12, boxHeight: 12 } } },
  scales: {
    x: { ticks: { color: MUTED }, grid: { display: false } },
    y: { beginAtZero: true, ticks: { precision: 0, color: MUTED }, grid: { color: LINE } },
  },
};

// Table columns: how each sorts. Newest week first by default.
const SORTS = {
  week: (w) => new Date(w.from).getTime(),
  retiro: (w) => w.retiro.dispatches,
  entrega: (w) => w.entrega.dispatches,
  perDelivery: (w) => w.tanksPerDelivery ?? -1,
  sinDato: (w) => w.sinDato.dispatches,
};

// Read-only: despachos of the last 8 weeks by how they left (GET /api/dispatches/weekly). Manifold keeps no truck routes
// or trips, so each entrega counts as one trip; despachos from before 23/09/2026 don't say and show as "sin dato".
const PickupsDeliveriesPanel = () => {
  const [weeks, setWeeks] = useState([]);
  const [sort, setSort] = useState({ key: 'week', dir: 'desc' });

  useEffect(() => {
    fetch('/api/dispatches/weekly').then((res) => res.json()).then((data) => setWeeks(Array.isArray(data?.weeks) ? data.weeks : []))
      .catch((err) => console.error('❌ Error fetching weekly dispatches:', err));
  }, []);

  const chartData = useMemo(() => ({
    labels: weeks.map((w) => `${dayLabel(w.from)}${w.partial ? ' (en curso)' : ''}`),
    datasets: [
      { label: 'Retirados en planta', data: weeks.map((w) => w.retiro.tanks), backgroundColor: SLATE, borderRadius: 4 },
      { label: 'Entregados por nuestro camión', data: weeks.map((w) => w.entrega.tanks), backgroundColor: CYAN, borderRadius: 4 },
    ],
  }), [weeks]);

  const sortedWeeks = useMemo(() => {
    const value = SORTS[sort.key];
    const sign = sort.dir === 'asc' ? 1 : -1;
    return [...weeks].sort((a, b) => sign * (value(a) - value(b)));
  }, [weeks, sort]);

  const handleSort = (key) => setSort((prev) => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));

  const SortHeader = ({ col, children, right = true }) => (
    <th className={right ? 'text-right' : undefined} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleSort(col)}>
      {children}
      <span style={{ marginLeft: 4, color: sort.key === col ? CYAN : MUTED, opacity: sort.key === col ? 1 : 0.4 }}>
        {sort.key === col && sort.dir === 'asc' ? '▲' : '▼'}
      </span>
    </th>
  );

  return (
    <div className="row mb-4">
      <div className="col-12">
        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Card.Title className="mb-0">Retiros y Entregas</Card.Title>
              <span className="text-muted" style={{ fontSize: '0.8rem' }}>Cilindros por semana (lunes a domingo), últimas 8 semanas</span>
            </div>
            <div style={{ height: 280 }}>
              <Bar data={chartData} options={CHART_OPTIONS} />
            </div>
            <Table size="sm" striped className="mt-3 mb-1">
              <thead>
                <tr>
                  <SortHeader col="week" right={false}>Semana del</SortHeader>
                  <SortHeader col="retiro">Despachos retiro</SortHeader>
                  <SortHeader col="entrega">Despachos entrega</SortHeader>
                  <SortHeader col="perDelivery">Promedio cil. por entrega</SortHeader>
                  <SortHeader col="sinDato">Sin dato</SortHeader>
                </tr>
              </thead>
              <tbody>
                {sortedWeeks.map((w) => (
                  <tr key={w.from}>
                    <td>{dayLabel(w.from)}{w.partial && <span className="text-muted"> (en curso)</span>}</td>
                    <td className="text-right">{w.retiro.dispatches}</td>
                    <td className="text-right">{w.entrega.dispatches}</td>
                    <td className="text-right">{w.tanksPerDelivery ?? '—'}</td>
                    <td className="text-right">{w.sinDato.dispatches ? `${w.sinDato.dispatches} (${w.sinDato.tanks} cil.)` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <small className="text-muted">
              Manifold no guarda rutas ni viajes del camión: cada despacho de entrega cuenta como un viaje. Los despachos de antes
              del 23/09/2026 no dicen si fueron entrega o retiro y salen como "sin dato" (no están en la gráfica).
            </small>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default PickupsDeliveriesPanel;
